import type { PaymentSource } from './payment';
import type { InvoiceReviewTarget, PublicInvoiceLearningReview } from './progress';

// Mirrors the Prisma InvoiceStatus enum
export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
}

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  /** True iff this line item was generated by chốt-sổ (linked to a BillingPeriod
   *  via BillingPeriod.invoiceItemId). Populated by the detail endpoint
   *  (GET /api/invoices/:id) only — listings leave it undefined. Billed items
   *  are read-only on the edit-DRAFT-invoice screen to protect the reconcile
   *  ledger link. */
  fromClosing?: boolean;
  /** The raw `is_generated` column, as returned by routes that emit the
   *  Prisma row unmapped — notably the public GET /by-token/:token that the
   *  payer's invoice page reads. The authenticated detail route remaps the
   *  same column to `fromClosing` above; both names refer to one column. */
  isGenerated?: boolean;
  /** Snapshot of the sessions behind a generated line, composed at chốt sổ.
   *  Null on manual lines and on invoices issued before the feature. */
  detailNote?: string | null;
}

/** One student behind a generated invoice line, aggregated from its items'
 *  linked `BillingPeriod → enrollment → student`. Powers the invoices list
 *  (display today; the client-side search box, THI-93, tomorrow) — nickname
 *  included because search matches it, not just the full name. */
export interface InvoiceStudentRef {
  name: string;
  nickname: string | null;
}

/** `GET /invoices` row: every base `Invoice` field plus `students`, deduped by
 *  student id across the invoice's items. `[]` for a manual invoice (no item
 *  links to a BillingPeriod) — always an array, never undefined, so callers
 *  never need `?? []`. Mirrors `Omit<Invoice, 'items'>` because the list route
 *  has never selected `items` (a per-invoice items list isn't needed there;
 *  `students` is aggregated separately, batched across the whole page). */
export interface InvoiceListItem extends Omit<Invoice, 'items'> {
  students: InvoiceStudentRef[];
  /** Always present on a list row — null when the invoice has no payments. */
  lastPayment: InvoiceLastPayment | null;
}

/** Flattened enrollment context behind ONE generated invoice item — student,
 *  subject, and the billed period's label ("2026-08" / "Khóa 3"). Strings,
 *  not nested entities: the one planned consumer (the chốt-sổ message
 *  adapter, THI-88 §2) needs names to interpolate into a sentence, not ids to
 *  look anything up further.
 *
 *  Null on manual lines AND on a generated line whose BillingPeriod slot was
 *  already claimed by a sibling item on the same enrollment/period — a period
 *  row links to exactly one InvoiceItem (`BillingPeriod.invoiceItemId`), and
 *  `generateMonthlyInvoices`/`renewTerm` (invoicingService.ts) always prefer
 *  the primary line (flat/prepay) over the "Bù trừ" adjustment line when both
 *  exist on the same period; the adjustment line only gets the link when it's
 *  the sole line for that enrollment/period (no prepay this cycle). See the
 *  `fromClosing` doc comment on `InvoiceItem` for the sibling note in
 *  apps/api's actual route comment. */
export interface InvoiceEnrollmentRef {
  studentName: string;
  studentNickname: string | null;
  subjectName: string;
  periodLabel: string;
}

/** `GET /invoices/:id` item: the base `InvoiceItem` plus `enrollmentRef`. */
export interface InvoiceItemWithEnrollmentRef extends InvoiceItem {
  enrollmentRef: InvoiceEnrollmentRef | null;
}

/** `GET /invoices/:id` response: `paidAmount` is always populated here
 *  (unlike the base `Invoice`'s optional field, shared with list rows that
 *  never carry it), and every item carries its `enrollmentRef`. */
export interface InvoiceDetail extends Omit<Invoice, 'items'> {
  items: InvoiceItemWithEnrollmentRef[];
  paidAmount: number;
  /** Always present on the detail response — null when no payments exist. */
  lastPayment: InvoiceLastPayment | null;
  /** Every (student, subject) billed by a MONTHLY BillingPeriod on this
   *  invoice, review-checklist-shaped (THI-189, spec §10.4a). Empty on a
   *  manual invoice — the detail page hides its review card on `[]`. */
  reviewTargets: InvoiceReviewTarget[];
}

/**
 * The most recent Payment linked to an invoice: when the money arrived and
 * where it came from. Null when nothing has been paid against it yet.
 *
 * Exists for one sentence on screen — "Tự khớp chuyển khoản lúc 09:41 hôm nay"
 * on a PAID invoice whose latest payment came from SePay (design §3). Manual
 * sources render nothing, but they are still reported here rather than
 * filtered server-side: the wire says what happened, the UI decides what is
 * worth saying.
 */
export interface InvoiceLastPayment {
  /**
   * `Payment.transactionDate`, ISO. A **real instant** — SePay's zone-less VN
   * local string is converted with `+07:00` before storing (see
   * apps/api/src/services/sepayIpnPayload.ts) — unlike the wall-clock-as-UTC
   * fields around it (`dueDate`, session `startAt`). Format it in
   * `Asia/Ho_Chi_Minh`, never with UTC getters.
   */
  at: string;
  source: PaymentSource;
}

export type InvoiceKind = 'CLOSE_MONTH' | 'TERM' | 'MANUAL';

/**
 * The invoice's kind from the `periodKind`s of the BillingPeriods its items
 * link to. MONTHLY wins over TERM on a mixed invoice: the monthly half is the
 * one carrying a bù-trừ month, and that is what the review surfaces anchor on.
 * Pure so the list route and its test share one rule (spec §11.6).
 */
export function invoiceKindFrom(periodKinds: readonly ('MONTHLY' | 'TERM')[]): InvoiceKind {
  if (periodKinds.length === 0) return 'MANUAL';
  return periodKinds.includes('MONTHLY') ? 'CLOSE_MONTH' : 'TERM';
}

export interface Invoice {
  id: string;
  invoiceCode: string;
  organizationId: string;
  clientId: string;
  subTotal: number;
  taxRate: number;
  totalAmount: number;
  /** Sum of all Payment.amount linked to this invoice. Populated by the detail
   *  endpoint (GET /api/invoices/:id) only — listings leave it undefined. */
  paidAmount?: number;
  /** Most recent linked payment. Populated by the list (GET /api/invoices) and
   *  detail (GET /api/invoices/:id) endpoints only — every other route that
   *  emits an invoice leaves it undefined, exactly like `paidAmount` above. */
  lastPayment?: InvoiceLastPayment | null;
  /** 'CLOSE_MONTH' when any linked BillingPeriod is MONTHLY (a chốt-sổ
   *  invoice), 'TERM' when every linked period is a khoá, 'MANUAL' when the
   *  invoice links none — derived from the items' periods, no schema change.
   *  Populated by the list endpoint (GET /api/invoices) only, exactly like
   *  `lastPayment` above (THI-189 §10.4b; TERM added THI-190 §11.6). */
  kind?: InvoiceKind;
  status: InvoiceStatus;
  /** Derived, never stored: true iff dueDate is in the past and status is still payable
   *  (PENDING/PARTIAL). See apps/api/src/services/invoiceStatus.ts. */
  isOverdue: boolean;
  /** Whole days past dueDate, floored, min 0. 0 when not overdue or dueDate is null. */
  daysOverdue: number;
  sepayTxnId?: string | null;
  dueDate: string | null;
  notes: string | null;
  /** Frozen at generation time (generateMonthlyInvoices / renewTerm): set iff this
   *  invoice bills an enrollment whose chargeAbsent was on when issued. Never
   *  derived at render — chargeAbsent is mutable after issue, so this must keep
   *  saying what was true at generation regardless of later edits. Null when no
   *  billed enrollment charged unexcused absences at the time. */
  absenceChargeNotice?: string | null;
  artifactData?: unknown | null;
  paymentRef?: string | null;
  publicToken: string;
  publicViewedAt: string | null;
  lastReminderSentAt: string | null;
  chatThreadId: string | null;
  artifactIndex: number | null;
  items: InvoiceItem[];
  createdAt: string;
  updatedAt: string;
  client?: any;
  user?: any;
}

/**
 * True once a manual deduction line (negative quantity, see
 * @repo/validation's invoiceItemSchema — used to net off tuition a teacher
 * already collected outside the app before onboarding) has brought
 * totalAmount to exactly zero. `<= 0` rather than `=== 0` defensively: the
 * server refuses to persist a negative total before it can ever reach here
 * (see NegativeInvoiceTotalError in apps/api/src/routes/invoices.ts), but a
 * caller working from a not-yet-validated draft should still treat "at or
 * below zero" as nothing owed rather than assume that invariant holds.
 *
 * There is nothing left to collect on such an invoice: a VietQR asking the
 * payer to transfer 0đ, or a paid ZNS message announcing it, is nonsense.
 * This is the single predicate every surface that offers to collect payment
 * consults — GET /:id/qr and POST /:id/send-zalo on the API, the dashboard's
 * SendInvoiceDialog and chat ArtifactPanel, and the public invoice page's
 * PaymentPanel — so none of them can drift out of agreement on the threshold.
 */
export function hasNothingToCollect(invoice: { totalAmount: number }): boolean {
  return Number(invoice.totalAmount) <= 0;
}

/**
 * What a payer still owes: the one number the QR, the public page, reminders
 * and debt figures all quote. `paid` is the sum of LINKED payments; with none
 * it is exactly the total, so an invoice nobody has paid into is unaffected.
 */
export function amountDue(total: number, paid: number): number {
  return Math.max(0, Number(total) - Number(paid));
}

/** Organization fields exposed on the public invoice page. Mirrors the
 *  `select` in GET /api/invoices/by-token/:token — that route is
 *  unauthenticated (the token is the credential), so it deliberately
 *  exposes nothing beyond what the payer needs to pay. */
export interface PublicInvoiceOrg {
  name: string;
  logoUrl: string | null;
  /** THI-263: `#rrggbb` or null; null below the Pro plan or when the centre never set one. */
  brandColor: string | null;
  bankCode: string | null;
  bankAccountNo: string | null;
  bankAccountName: string | null;
  /** False only on plans that suppress the LopHoc.app footer badge.
   *  The endpoint fails closed to `true` when the plan can't be read. */
  showsWatermark: boolean;
}

export interface PublicInvoiceClient {
  name: string;
  email: string | null;
}

/** One invoice the bulk action did NOT act on, and why. `code` is stable for
 *  the FE to branch on; `reason` is the Vietnamese string the confirm summary
 *  can show as-is. */
export interface BulkInvoiceActionSkipped {
  id: string;
  code: 'ALREADY_PAID' | 'CANCELLED' | 'NOT_DRAFT';
  reason: string;
}

/** Response of `POST /invoices/bulk/mark-paid`. */
export interface BulkMarkPaidResult {
  marked: number;
  skipped: BulkInvoiceActionSkipped[];
}

/** Response of `POST /invoices/bulk/delete`. */
export interface BulkDeleteInvoicesResult {
  deleted: number;
  skipped: BulkInvoiceActionSkipped[];
}

/** Response of `POST /invoices/bulk/due-date` (THI-100) — DRAFT-only re-date;
 *  reuses `BulkInvoiceActionSkipped`'s `NOT_DRAFT` code, same as bulk delete. */
export interface BulkDueDateInvoicesResult {
  updated: number;
  skipped: BulkInvoiceActionSkipped[];
}

/** Exactly what GET /api/invoices/by-token/:token returns.
 *
 *  Deliberately an explicit field list rather than `Omit<Invoice, …>`. That
 *  route is **unauthenticated** — anyone holding the shareable link reads it —
 *  and it used to spread the whole Prisma row, which shipped `artifactData`
 *  (a snapshot of the AI-generated artifact), `chatThreadId`, `artifactIndex`,
 *  `sepayTxnId`, `organizationId`, `clientId`, `publicToken`, `publicViewedAt`
 *  and `lastReminderSentAt` to the payer. Listing the fields means a column
 *  added to `Invoice` later cannot silently join them: it has to be named here
 *  and in the route's response, both times on purpose.
 *
 *  `paymentRef` is intentionally absent: the server needs it to build `qrUrl`
 *  and `transferContent`, but the page renders those, never the bare ref. */
export interface PublicInvoice {
  id: string;
  invoiceCode: string;
  status: InvoiceStatus;
  subTotal: number;
  taxRate: number;
  totalAmount: number;
  /** Sum of LINKED payments. The amount the page and QR ask for is
   *  `amountDue(totalAmount, paidAmount)`. */
  paidAmount: number;
  dueDate: string | null;
  notes: string | null;
  /** See `Invoice.absenceChargeNotice`'s doc comment — the payer-facing surface
   *  of the same frozen, generation-time policy statement. */
  absenceChargeNotice: string | null;
  createdAt: string;
  items: InvoiceItem[];
  organization: PublicInvoiceOrg;
  client: PublicInvoiceClient;
  /** Built server-side: the live IPN keyword lives in the database, which the
   *  public site cannot read. Null when the org has no linked bank account or
   *  the bank has no known VietQR BIN. */
  qrUrl: string | null;
  /** The exact memo text a payer must use, matching what the QR encodes. */
  transferContent: string | null;
  /**
   * THI-105. LIVE-fetched at render, never snapshotted onto the invoice —
   * deliberately unlike the frozen money narrative beside it (INVARIANTS §1).
   * The frozen rule protects what was BILLED; a review is prose, and a teacher
   * fixing a typo after sending the invoice SHOULD reach the parent.
   *
   * The month shown is the invoice's bù-trừ month (the month before its
   * billing month) — the completed month the parent is reading about. Empty
   * when that month has no reviews, and the whole section then disappears.
   */
  learningReviews: PublicInvoiceLearningReview[];
}
